<template>
  <el-dialog
    v-if="isPasswordModalDisplayed"
    v-model="isDisplayed"
    class="password-modal"
    close-on-click-modal
    modal-class="center-with-menu"
    body-class="password-modal__dialog">
    <template #header>
      <h3 class="password-modal__dialog__title">
        {{ $t('profile.newPassword.title1') }}
      </h3>
    </template>
    <el-form
      v-loading="isProfileLoading"
      :model="form"
      :rules="rules"
      @submit.prevent="submit"
      ref="ruleForm">
      <el-row :gutter="50">
        <el-col :xs="24" :sm="12">
          <el-form-item :label="$t('profile.newPassword.email.text')" label-position="top">
            <el-input
              v-model="email"
              :placeholder="$t('profile.newPassword.email.text')"
              :suffix-icon="messageIcon"
              disabled>
            </el-input>
          </el-form-item>
          <el-form-item :label="$t('profile.newPassword.newPassword.text')" prop="newPassword" label-position="top">
            <el-input
              v-model="form.newPassword"
              :placeholder="$t('profile.newPassword.newPassword.text')"
              show-password
              :suffix-icon="lockIcon"
              autocomplete="new-password">
            </el-input>
          </el-form-item>
          <el-form-item
            :label="$t('profile.newPassword.newPasswordConfirmation.text')"
            prop="newPasswordConfirmation"
            label-position="top">
            <el-input
              v-model="form.newPasswordConfirmation"
              :placeholder="$t('profile.newPassword.newPasswordConfirmation.text')"
              autocomplete="new-password"
              show-password
              :suffix-icon="lockIcon">
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :xs="24" :sm="12" class="password-modal__dialog__rules">
          <p>{{ $t('profile.newPassword.newPassword.rules.title') }}</p>
          <ul v-if="profile && profile.is_minarm">
            <li>{{ $t('profile.newPassword.newPassword.rules.lengthCondition') }}</li>
            <li>{{ $t('profile.newPassword.newPassword.rules.caseCondition') }}</li>
            <li>{{ $t('profile.newPassword.newPassword.rules.specialCondition') }}</li>
          </ul>
          <ul v-else-if="profile && !profile.is_minarm">
            <li>{{ $t('profile.newPassword.newPassword.rules.lengthCondition2') }}</li>
          </ul>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="isDisplayed = false">
          {{ $t('base.ignore') }}
        </el-button>
        <el-button
          type="primary"
          @click="submit"
          :loading="isPostPasswordLoading"
          :disabled="isProfileLoading || isPostPasswordLoading"
        >
          {{ $t('profile.newPassword.submit.new') }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useProfileStore, useActionsStore, useDemandStore } from '@/stores';
import { Lock, Message } from '@element-plus/icons-vue';

export default {
  name: 'PasswordModal',
  data() {
    const self = this;
    const regexValidation = (rule, value, callback) => {
      try {
        if (!self.profile?.is_minarm || rule.regex.test(value)) {
          return callback();
        }
        return callback(new Error());
      } catch (error) {
        return callback();
      }
    };
    const equalPasswordValidation = (rule, value, callback) => {
      try {
        if (self.form.newPassword !== value) {
          return callback(new Error());
        }
        return callback();
      } catch (error) {
        return callback();
      }
    };
    const lengthValidation = (rule, value, callback) => {
      try {
        if ((self.profile?.is_minarm && value.length) > 9
            || (!self.profile?.is_minarm && value.length) > 7) {
          return callback();
        }
        return callback(self.profile?.is_minarm
          ? self.$t('profile.newPassword.newPassword.lengthCondition')
          : self.$t('profile.newPassword.newPassword.lengthCondition2'));
      } catch (error) {
        return callback();
      }
    };
    return {
      form: {
        newPassword: '',
        newPasswordConfirmation: '',
      },
      rules: {
        newPassword: [
          {
            required: true,
            message: self.$t('profile.newPassword.newPassword.warning'),
            trigger: 'blur',
          },
          {
            validator: lengthValidation,
            trigger: 'blur',
          },
          {
            validator: regexValidation,
            regex: /^(?=.*[a-z])(?=.*[A-Z]).+$/,
            message: self.$t('profile.newPassword.newPassword.caseCondition'),
            trigger: 'blur',
          },
          {
            validator: regexValidation,
            regex: /[ !@#$%^&*()_+\-=[\]{};':"|,.<>/?]/,
            message: self.$t('profile.newPassword.newPassword.specialCondition'),
            trigger: 'blur',
          },
        ],
        newPasswordConfirmation: [
          {
            required: true,
            message: self.$t('profile.newPassword.newPasswordConfirmation.warning'),
            trigger: 'blur',
          },
          {
            validator: equalPasswordValidation,
            message: self.$t('profile.newPassword.newPasswordConfirmation.passwordConfirmationEqual'),
            trigger: 'blur',
          },
        ],
      },
    };
  },
  computed: {
    ...mapState(useProfileStore, ['profile', 'isProfileLoading', 'isPostPasswordLoading']),
    ...mapState(useActionsStore, ['isPasswordModalDisplayed']),
    lockIcon() {
      return Lock;
    },
    messageIcon() {
      return Message;
    },
    isDisplayed: {
      get() {
        return this.isPasswordModalDisplayed;
      },
      set(display) {
        if (display) {
          this.displayPasswordModal();
        } else {
          this.hidePasswordModal();
        }
      },
    },
    email() {
      return this.profile ? this.profile.email : '';
    },
  },
  methods: {
    ...mapActions(useDemandStore, ['getDemand']),
    ...mapActions(useActionsStore, ['displayPasswordModal', 'hidePasswordModal']),
    ...mapActions(useProfileStore, ['postPassword']),
    submit() {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.validate((valid) => {
          if (valid) {
            this.postPassword({
              new_password: this.form.newPassword,
            }).then(() => {
              this.getDemand();
              this.isDisplayed = false;
              this.form = {
                newPassword: '',
                newPasswordConfirmation: '',
              };
            }).catch((error) => error);
          }
        });
      }
    },
  },
};
</script>

<style lang="scss">
.password-modal {
  margin-top: 5vh!important;
  width: 90%;
  @include breakpoint(tablet) {
    width: 75%;
  }
  .password-modal__dialog__title {
    margin-right: 20px;
    margin-top: 0;
  }
  .el-dialog__body {
    padding-top: 10px;
  }
  .password-modal__dialog__rules {
    margin-top: 8px;
    word-break: normal;
  }
}
</style>
